import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddressAutocomplete, { AddressValues } from '../AddressAutocomplete';

export interface ServiceAddressOrderProps {
  value: string;
  onChange: (address: string) => void;
  disabled?: boolean;
}

function formatAddressFromValues(v: AddressValues): string {
  const line1 = [v.address_line1, v.address_line2].filter(Boolean).join(', ');
  const loc = [v.address_suburb, v.address_state, v.address_postcode].filter(Boolean).join(' ');
  const parts = [line1, loc, v.address_country].filter(Boolean);
  return parts.join(', ') || (v.address_formatted ?? '');
}

const COLLAPSE_DELAY_MS = 2000;

/**
 * Service address above plan selection: Google Places autocomplete (AU addresses)
 * plus a single editable "service address" line. When user enters an address,
 * Step 1 collapses after 2s to save space; the Service address field stays visible.
 */
function ServiceAddressOrder({ value, onChange, disabled = false }: ServiceAddressOrderProps) {
  const [addressValues, setAddressValues] = useState<AddressValues>({});
  const [addressLine, setAddressLine] = useState(value || '');
  const [collapsed, setCollapsed] = useState(false);
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (value) setAddressLine(value);
  }, [value]);

  // Collapse Step 1 only when user selects an address from the Google dropdown (after 2s delay)
  const handlePlaceSelectedFromGoogle = () => {
    if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);
    collapseTimerRef.current = setTimeout(() => {
      setCollapsed(true);
      collapseTimerRef.current = null;
    }, COLLAPSE_DELAY_MS);
  };

  useEffect(() => {
    return () => {
      if (collapseTimerRef.current) clearTimeout(collapseTimerRef.current);
    };
  }, []);

  const handleStructuredChange = (v: AddressValues) => {
    setAddressValues(v);
    const formatted = formatAddressFromValues(v);
    if (formatted) {
      setAddressLine(formatted);
      onChange(formatted);
    }
  };

  const handleAddressLineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setAddressLine(next);
    onChange(next);
  };

  const displayAddress = (addressLine || value || '').trim();

  return (
    <Box sx={{ mb: 4 }}>
      <Accordion
        expanded={!collapsed}
        onChange={() => setCollapsed((prev) => !prev)}
        disableGutters
        sx={{ '&:before': { display: 'none' }, boxShadow: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%', pr: 1 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Step 1 – Enter your service address
            </Typography>
            {collapsed && displayAddress && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
                {displayAddress}
              </Typography>
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Search with Google (Australia) or type your address. You can edit or overwrite the address below at any time.
          </Typography>
          <AddressAutocomplete
            value={addressValues}
            onChange={handleStructuredChange}
            disabled={disabled}
            line1Label="Street address"
            onPlaceSelected={handlePlaceSelectedFromGoogle}
          />
        </AccordionDetails>
      </Accordion>

      <TextField
        fullWidth
        required
        label="Service address (editable – overwrite if needed)"
        placeholder="Full address where service will be installed"
        value={addressLine}
        onChange={handleAddressLineChange}
        disabled={disabled}
        multiline
        minRows={1}
        maxRows={3}
        sx={{ mt: 2 }}
        helperText="This is the address we will use for the order. Change it here if the suggested address is wrong."
      />
    </Box>
  );
}

export default ServiceAddressOrder;
